
import UIEvents from '../../../../service/UI/UIEvents';
import { openEtherPadFinal } from '../../etherpad';
import { hideNotification } from '../../notifications';
import { isPrejoinPageVisible } from '../../prejoin/functions';
import { openWhiteBoardFinal } from '../../toolbox/actions.any';
import { setShareMenuVisible } from '../../toolbox/actions.web';
import { toggleBackgroundDevice } from '../../virtual-background/actions';
import { getAvailableDevices, notifyCameraError, notifyMicError } from '../devices/actions';
import {
    SET_AUDIO_MUTED,
    SET_CAMERA_FACING_MODE,
    SET_SCREENSHARE_MUTED,
    SET_VIDEO_MUTED,
    TOGGLE_CAMERA_FACING_MODE
} from '../media/actionTypes';
import { gumPending, setScreenshareMuted, toggleCameraFacingMode } from '../media/actions';
import { CAMERA_FACING_MODE, MEDIA_TYPE, VIDEO_TYPE } from '../media/constants';
import { IGUMPendingState } from '../media/types';
import {
    isLocalParticipantForceMutedAudio,
    isLocalParticipantForceMutedVideo
} from '../participants';
import { MiddlewareRegistry } from '../redux';
import { SETTINGS_UPDATED } from '../settings/actionTypes';

import {
    TOGGLE_SCREENSHARING,
    TRACK_ADDED,
    TRACK_MUTE_UNMUTE_FAILED,
    TRACK_NO_DATA_FROM_SOURCE,
    TRACK_REMOVED,
    TRACK_STOPPED,
    TRACK_UPDATED
} from './actionTypes';
import {
    addLocalTrack,
    createLocalTracksA,
    replaceLocalTrack,
    showNoDataFromSourceVideoError,
    trackMuteUnmuteFailed,
    trackNoDataFromSourceNotificationInfoChanged,
    trackRemoved
} from './actions';
import {
    createLocalTracksF,
    getLocalTrack,
    getTrackByMHTrack,
    isExpectedTrackIssue,
    isUserInteractionRequiredForUnmute,
    logTracksForParticipant,
    setTrackMuted
} from './functions';
import logger from './logger';
import { toggleScreensharing } from './mhActions';


import './subscriber';

declare var APP: Object;

const LOCAL_UNMUTE_RECOVERY_THROTTLE_MS = 5000;
const _lastLocalUnmuteRecoveryAt = {
    audio: 0,
    video: 0
};

/**
 * Middleware that captures LIB_DID_DISPOSE and LIB_DID_INIT actions and,
 * respectively, creates/destroys local media tracks. Also listens to
 * media-related actions and performs corresponding operations with tracks.
 *
 * @param {Store} store - The redux store.
 * @returns {Function}
 */
MiddlewareRegistry.register(store => next => action => {
    switch (action.type) {
    case TRACK_ADDED: {
        // The devices list needs to be refreshed when no initial video permissions
        // were granted and a local video track is added by umuting the video.
        if (action.track.local) {
            store.dispatch(getAvailableDevices());

            // Apply virtual background effect to new track if enabled
            store.dispatch(toggleBackgroundDevice(action.track));
        }

        const result = next(action);
        const participantId = action.track?.participantId;

        if (participantId) {
            logTracksForParticipant(store.getState()['features/base/tracks'], participantId, 'Track added');
        }

        // Safety recheck for remote video tracks that arrive muted due to
        // missing peerMediaInfo at track creation time. When a remote
        // participant joins late, signaling info may arrive after the track,
        // causing it to be created with muted=true. If TRACK_MUTE_CHANGED
        // is lost or delayed, the video stays black. This delayed recheck
        // queries the actual mhTrack state and corrects Redux if needed.
        if (!action.track.local
            && action.track.muted
            && (action.track.mediaType === MEDIA_TYPE.VIDEO
                || action.track.mediaType === MEDIA_TYPE.SCREENSHARE)) {
            const mhTrack = action.track.mhTrack;

            if (mhTrack) {
                setTimeout(() => {
                    const actualMuted = mhTrack.isMuted();

                    if (!actualMuted) {
                        store.dispatch({
                            type: TRACK_UPDATED,
                            track: {
                                mhTrack,
                                muted: false
                            }
                        });
                    }
                }, 1000);
            }
        }

        return result;
    }
    case TRACK_NO_DATA_FROM_SOURCE: {
        const result = next(action);

        _handleNoDataFromSourceErrors(store, action);

        return result;
    }
    case TRACK_REMOVED: {
        _removeNoDataFromSourceNotification(store, action.track);
        break;
    }
    case SET_AUDIO_MUTED: {
        const state = store.getState();

        if (!action.muted
                && (isUserInteractionRequiredForUnmute(state)
                    || state['features/base/conference']?.meetingLimitExceeded
                    || isLocalParticipantForceMutedAudio(state))) {
            return;
        }

        _setMuted(store, action, MEDIA_TYPE.AUDIO);
        break;
    }

    case SET_CAMERA_FACING_MODE: {
        // XXX The camera facing mode of a MediaStreamTrack can be specified
        // only at initialization time and then it can only be toggled. So in
        // order to set the camera facing mode, one may destroy the track and
        // then initialize a new instance with the new camera facing mode. But
        // that is inefficient on mobile at least so the following relies on the
        // fact that there are 2 camera facing modes and merely toggles between
        // them to (hopefully) get the camera in the specified state.
        const localTrack = _getLocalTrack(store, MEDIA_TYPE.VIDEO);
        let mhTrack;

        if (localTrack
                && (mhTrack = localTrack.mhTrack)
                && mhTrack.getCameraFacingMode()
                    !== action.cameraFacingMode) {
            store.dispatch(toggleCameraFacingMode());
        }
        break;
    }

    case SET_VIDEO_MUTED: {
        const state = store.getState();

        if (!action.muted
                && (isUserInteractionRequiredForUnmute(state)
                    || state['features/base/conference']?.meetingLimitExceeded
                    || isLocalParticipantForceMutedVideo(state))) {
            return;
        }

        _setMuted(store, action, action.mediaType);
        break;
    }

    case TOGGLE_CAMERA_FACING_MODE: {
        const localTrack = _getLocalTrack(store, MEDIA_TYPE.VIDEO);
        let mhTrack;

        if (localTrack && (mhTrack = localTrack.mhTrack)) {
            // XXX MediaStreamTrack._switchCamera is a custom function
            // implemented in react-native-webrtc for video which switches
            // between the cameras via a native WebRTC library implementation
            // without making any changes to the track.
            mhTrack._switchCamera();

            // Don't mirror the video of the back/environment-facing camera.
            // Also respect the localFlipX setting.
            const isUserFacing
                = mhTrack.getCameraFacingMode() === CAMERA_FACING_MODE.USER;
            const { localFlipX } = store.getState()['features/base/settings'];
            const mirror = isUserFacing && localFlipX !== false;

            store.dispatch({
                type: TRACK_UPDATED,
                track: {
                    mhTrack,
                    mirror
                }
            });
        }
        break;
    }

    case SETTINGS_UPDATED: {
        // When localFlipX changes, update the mirror property of the local video track.
        if ('localFlipX' in action.settings) {
            const localTrack = _getLocalTrack(store, MEDIA_TYPE.VIDEO);

            if (localTrack && localTrack.mhTrack) {
                const mhTrack = localTrack.mhTrack;
                const isUserFacing
                    = mhTrack.getCameraFacingMode() === CAMERA_FACING_MODE.USER;
                const mirror = isUserFacing && action.settings.localFlipX !== false;

                store.dispatch({
                    type: TRACK_UPDATED,
                    track: {
                        mhTrack,
                        mirror
                    }
                });
            }
        }
        break;
    }

    case TRACK_MUTE_UNMUTE_FAILED: {
        const { mhTrack } = action.track;

        // The action is created with `wasMuting` (see trackMuteUnmuteFailed);
        // reading `wasMuted` always yielded undefined, which made `!muted` below
        // unconditionally true and ran unmute recovery on failed *mute* attempts
        // as well.
        const muted = action.wasMuting;
        const isVideoTrack = mhTrack?.getType() !== MEDIA_TYPE.AUDIO;

        // Auto-recover from local unmute failures by recreating/replacing the track once.
        // This addresses stale/locked devices where users currently have to reselect manually.
        if (!muted && mhTrack?.isLocal?.()) {
            _recoverLocalTrackFromUnmuteFailure(store, mhTrack);
        }

        if (isVideoTrack && mhTrack?.getVideoType() === VIDEO_TYPE.DESKTOP) {
            store.dispatch(setScreenshareMuted(!muted));
        } else if (isVideoTrack && APP !== undefined) {
            APP.conference.setVideoMuteStatus();
        } else if (APP !== undefined) {
            APP.conference.updateAudioIconEnabled();
        }

        break;
    }


    case TRACK_STOPPED: {
        const { mhTrack } = action.track;

        if (mhTrack.getVideoType() === VIDEO_TYPE.DESKTOP) {
            store.dispatch(toggleScreensharing(false));
        } else if (mhTrack.isLocal() && mhTrack.isVideoTrack()) {
            // Local camera track ended (OS killed it during phone call,
            // lock, or background).  Mark it muted so the UI shows an
            // avatar immediately instead of a frozen/black frame.
            store.dispatch({
                type: TRACK_UPDATED,
                track: {
                    mhTrack,
                    muted: true
                }
            });
        }
        break;
    }

    case SET_SCREENSHARE_MUTED:
        _setMuted(store, action, MEDIA_TYPE.SCREENSHARE);
        break;

    case TOGGLE_SCREENSHARING:
        if (typeof APP === 'object') {
            store.dispatch(setShareMenuVisible(!store.getState()['features/toolbox'].visibility));
            APP.UI.emitEvent(UIEvents.TOGGLE_SCREENSHARING);
        }
        break;

    case TRACK_UPDATED:
        // TODO Remove the following calls to APP.UI once components interested
        // in track mute changes are moved into React and/or redux.
        if (typeof APP !== 'undefined') {
            const result = next(action);

            if (isPrejoinPageVisible(store.getState())) {
                return result;
            }

            const { mhTrack } = action.track;
            const participantID = mhTrack.getParticipantId();
            const isVideoTrack = mhTrack.type !== MEDIA_TYPE.AUDIO;
            const local = mhTrack.isLocal();

            if (isVideoTrack) {
                if (local && !(mhTrack.getVideoType() === VIDEO_TYPE.DESKTOP)) {
                    APP.conference.setVideoMuteStatus();
                } else if (!local) {
                    APP.UI.setVideoMuted(participantID);
                }
            } else if (local) {
                APP.conference.updateAudioIconEnabled();
            }


            const participants = store.getState()['features/base/participants'];
            const etherpad = store.getState()['features/etherpad'];
            const tooltbox = store.getState()['features/toolbox'];


            if (tooltbox.actionToggleWhiteBoard && participants.length === 1) {
                store.dispatch(openWhiteBoardFinal(tooltbox.openWhiteBoard));
            }

            if (etherpad.actionToggleEtherPad && participants.length === 1) {
                store.dispatch(openEtherPadFinal(etherpad.openEtherPad));
            }

            return result;
        }

    }

    return next(action);
});

/**
 * Handles no data from source errors.
 *
 * @param {Store} store - The redux store in which the specified action is
 * dispatched.
 * @param {Action} action - The redux action dispatched in the specified store.
 * @private
 * @returns {void}
 */
function _handleNoDataFromSourceErrors(store, action) {
    const { getState, dispatch } = store;

    const track = getTrackByMHTrack(getState()['features/base/tracks'], action.track.mhTrack);

    if (!track || !track.local) {
        return;
    }

    const { mhTrack } = track;

    if (track.mediaType === MEDIA_TYPE.AUDIO && track.isReceivingData) {
        _removeNoDataFromSourceNotification(store, action.track);
    }

    if (track.mediaType === MEDIA_TYPE.VIDEO) {
        const { noDataFromSourceNotificationInfo = {} } = track;

        if (track.isReceivingData) {
            if (noDataFromSourceNotificationInfo.timeout) {
                clearTimeout(noDataFromSourceNotificationInfo.timeout);
                dispatch(trackNoDataFromSourceNotificationInfoChanged(mhTrack, undefined));
            }

            // try to remove the notification if there is one.
            _removeNoDataFromSourceNotification(store, action.track);
        } else {
            if (noDataFromSourceNotificationInfo.timeout) {
                return;
            }

            const timeout = setTimeout(() => dispatch(showNoDataFromSourceVideoError(mhTrack)), 5000);

            dispatch(trackNoDataFromSourceNotificationInfoChanged(mhTrack, { timeout }));
        }
    }
}

/**
 * Gets the local track associated with a specific {@code MEDIA_TYPE} in a
 * specific redux store.
 *
 * @param {Store} store - The redux store from which the local track associated
 * with the specified {@code mediaType} is to be retrieved.
 * @param {MEDIA_TYPE} mediaType - The {@code MEDIA_TYPE} of the local track to
 * be retrieved from the specified {@code store}.
 * @param {boolean} [includePending] - Indicates whether a local track is to be
 * returned if it is still pending. A local track is pending if
 * {@code getUserMedia} is still executing to create it and, consequently, its
 * {@code mhTrack} property is {@code undefined}. By default a pending local
 * track is not returned.
 * @private
 * @returns {Track} The local {@code Track} associated with the specified
 * {@code mediaType} in the specified {@code store}.
 */
function _getLocalTrack(
        { getState }: { getState: Function },
        mediaType: MEDIA_TYPE,
        includePending: boolean = false) {
    return (
        getLocalTrack(
            getState()['features/base/tracks'],
            mediaType,
            includePending));
}

/**
 * Removes the no data from source notification associated with the MHTrack if displayed.
 *
 * @param {Store} store - The redux store.
 * @param {Track} track - The redux action dispatched in the specified store.
 * @returns {void}
 */
function _removeNoDataFromSourceNotification({ getState, dispatch }, track) {
    const t = getTrackByMHTrack(getState()['features/base/tracks'], track.mhTrack);
    const { mhTrack, noDataFromSourceNotificationInfo = {} } = t || {};

    if (noDataFromSourceNotificationInfo && noDataFromSourceNotificationInfo.uid) {
        dispatch(hideNotification(noDataFromSourceNotificationInfo.uid));
        dispatch(trackNoDataFromSourceNotificationInfoChanged(mhTrack, undefined));
    }
}

/**
 * Mutes or unmutes a local track with a specific media type.
 *
 * @param {Store} store - The redux store in which the specified action is
 * dispatched.
 * @param {Action} action - The redux action dispatched in the specified store.
 * @param {MEDIA_TYPE} mediaType - The {@link MEDIA_TYPE} of the local track
 * which is being muted or unmuted.
 * @private
 * @returns {void}
 */
function _setMuted(store, { ensureTrack, muted }, mediaType: MEDIA_TYPE) {
    const { dispatch, getState } = store;
    const localTrack = _getLocalTrack(store, mediaType, /* includePending */ true);
    const state = getState();

    if (mediaType === MEDIA_TYPE.SCREENSHARE && !muted) {
        return;
    }

    if (localTrack) {
        // The `mhTrack` property will have a value only for a localTrack for which `getUserMedia` has already
        // completed. If there's no `mhTrack`, then the `muted` state will be applied once the `mhTrack` is
        // created.
        const { mhTrack } = localTrack;

        if (mhTrack) {
            setTrackMuted(mhTrack, muted, state, dispatch)
                .catch(() => dispatch(trackMuteUnmuteFailed(localTrack, muted)));
        }
    } else if (!muted && ensureTrack && (typeof APP === 'undefined' || isPrejoinPageVisible(state))) {
        // FIXME: This only runs on mobile now because web has its own way of
        // creating local tracks. Adjust the check once they are unified.
        //
        // Do NOT widen this to web-in-conference: muteLocal() dispatches
        // setAudioMuted(_, ensureTrack) *and* emits UIEvents.AUDIO_MUTED, which
        // conference.js turns into muteAudio() -> createLocalTracksF() for the very
        // same device (conference.js:1904). Running both would fire two concurrent
        // gUM calls and trip 'Cannot add second audio track to the conference'.
        typeof APP !== 'undefined' && dispatch(gumPending([ mediaType ], IGUMPendingState.PENDING_UNMUTE));

        const clearGUMPending = () =>
            typeof APP !== 'undefined' && dispatch(gumPending([ mediaType ], IGUMPendingState.NONE));

        dispatch(createLocalTracksA({ devices: [ mediaType ] }))
            .then(clearGUMPending, error => {
                // Without this the pending state would stick and leave the control
                // disabled, which is the very symptom being fixed here.
                clearGUMPending();
                logger.error(`Failed to create local ${mediaType} track on unmute`, error);
            });
    }
}

/**
 * Recreates and replaces the failed local camera/mic track after an unmute failure.
 *
 * @param {Store} store - The redux store.
 * @param {MHLocalTrack} failedTrack - The local track that failed to unmute.
 * @returns {void}
 */
function _recoverLocalTrackFromUnmuteFailure(store, failedTrack) {
    if (!failedTrack?.isLocal?.()) {
        return;
    }

    const mediaType = failedTrack.getType();

    if (mediaType !== MEDIA_TYPE.AUDIO && mediaType !== MEDIA_TYPE.VIDEO) {
        return;
    }

    if (failedTrack.getVideoType?.() === VIDEO_TYPE.DESKTOP) {
        return;
    }

    const now = Date.now();
    const recoveryKey = mediaType === MEDIA_TYPE.AUDIO ? 'audio' : 'video';

    if (now - _lastLocalUnmuteRecoveryAt[recoveryKey] < LOCAL_UNMUTE_RECOVERY_THROTTLE_MS) {
        return;
    }

    _lastLocalUnmuteRecoveryAt[recoveryKey] = now;

    const { dispatch, getState } = store;
    const createOptions = {
        devices: [ mediaType ]
    };

    dispatch(gumPending([ mediaType ], IGUMPendingState.PENDING_UNMUTE));

    createLocalTracksF(createOptions, {
        dispatch,
        getState
    })
        .then(newTracks => {
            const newTrack = newTracks?.[0];

            if (!newTrack) {
                return Promise.reject(new Error(`Track recovery failed: no replacement ${mediaType} track`));
            }

            // A disposed track cannot be the oldTrack of a replace - MHConference.replaceTrack rejects it
            // outright - so drop the dead one from the store and add the fresh one on its own instead.
            // This path used to bail out before creating anything, which meant an unmute that failed
            // because the device had already been disposed reported the failure and then never repaired
            // it. Surfacing that failure is the whole reason setTrackMuted stopped swallowing
            // TRACK_IS_DISPOSED, so declining to act on it left the fix half-done: the control still went
            // dead, it just logged on the way. Re-checked here rather than up front because the track can
            // also be disposed while the replacement gUM is in flight.
            if (failedTrack.disposed) {
                logger.warn(`Recovering a disposed local ${mediaType} track by adding a replacement`);

                dispatch(trackRemoved(failedTrack));

                return dispatch(addLocalTrack(newTrack));
            }

            return dispatch(replaceLocalTrack(failedTrack, newTrack));
        })
        .catch(error => {
            logger.error(`Automatic ${mediaType} unmute recovery failed`, error);

            // Recovery fails when the device itself is gone (unplugged, taken by another app, permission
            // revoked). Without this the button just flips back and the user is left guessing, since the only
            // trace of the failure is in the console. Track creation already shows its own message for the
            // device problems it recognises, so only the remaining failures are reported here - otherwise the
            // user gets two notifications for one failed unmute.
            if (!isExpectedTrackIssue(error)) {
                dispatch(mediaType === MEDIA_TYPE.AUDIO ? notifyMicError(error) : notifyCameraError(error));
            }
        })
        .finally(() => {
            dispatch(gumPending([ mediaType ], IGUMPendingState.NONE));
        });
}
